// Lightweight games registry — metadata ONLY (no React Page component).
//
// Used by admin/agent shells to know what games exist and how to theme
// the per-game admin config page. The player-frontend has its own
// games/registry.ts with the full GameModule including each game's
// gameplay Page.
//
// To add a new game: append its metadata here AND add its Page in
// player-frontend's games/<id>/index.ts. Both registries stay in sync
// because they describe the same set of game ids.
import type { GameModule } from './types';

// Metadata-only GameModule (Page omitted). All admin-facing fields kept.
type GameMeta = Omit<GameModule, 'Page'>;

const ludo: GameMeta = {
  id: 'ludo', displayName: 'Ludo', tag: 'Board',
  theme: { gradient: 'from-purple-600 to-pink-900', emoji: '🎲', logo: '/games/ludo/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-purple-900', headerTo: 'to-fuchsia-800',
    ring: 'ring-fuchsia-400', accent: 'bg-fuchsia-600', accentHover: 'hover:bg-fuchsia-500',
    dot: 'bg-fuchsia-400', softBg: 'bg-purple-900/30', rangeAccent: 'accent-fuchsia-500',
    badgeClasses: 'bg-purple-600/20 text-purple-300 border-purple-600/30',
  },
};

const xo: GameMeta = {
  id: 'xo', displayName: 'Tic Tac Toe', tag: 'Strategy',
  theme: { gradient: 'from-teal-500 to-emerald-800', emoji: '⨯', logo: '/games/xo/Logo.png' },
  adminIcon: 'apps',
  adminTheme: {
    headerFrom: 'from-cyan-900', headerTo: 'to-blue-800',
    ring: 'ring-cyan-400', accent: 'bg-cyan-600', accentHover: 'hover:bg-cyan-500',
    dot: 'bg-cyan-400', softBg: 'bg-cyan-900/30', rangeAccent: 'accent-cyan-500',
    badgeClasses: 'bg-cyan-600/20 text-cyan-300 border-cyan-600/30',
  },
};

const rummy: GameMeta = {
  id: 'rummy', displayName: 'Rummy', tag: 'Card',
  theme: { gradient: 'from-green-600 via-green-800 to-green-950', emoji: '🃏', logo: '/games/rummy/Logo.png' },
  adminIcon: 'style',
  adminTheme: {
    headerFrom: 'from-emerald-900', headerTo: 'to-teal-800',
    ring: 'ring-emerald-400', accent: 'bg-emerald-600', accentHover: 'hover:bg-emerald-500',
    dot: 'bg-emerald-400', softBg: 'bg-emerald-900/30', rangeAccent: 'accent-emerald-500',
    badgeClasses: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/30',
  },
};

const chess: GameMeta = {
  id: 'chess', displayName: 'Chess', tag: 'Strategy',
  theme: { gradient: 'from-[#b58863] to-[#3a2515]', emoji: '♟️', logo: '/games/chess/Logo.png' },
  adminIcon: 'mediation',
  adminTheme: {
    headerFrom: 'from-slate-900', headerTo: 'to-stone-800',
    ring: 'ring-slate-400', accent: 'bg-slate-600', accentHover: 'hover:bg-slate-500',
    dot: 'bg-slate-400', softBg: 'bg-slate-900/30', rangeAccent: 'accent-slate-500',
    badgeClasses: 'bg-slate-600/20 text-slate-300 border-slate-600/30',
  },
};

const carrom: GameMeta = {
  id: 'carrom', displayName: 'Carrom', tag: 'Board',
  theme: { gradient: 'from-cyan-400 to-blue-900', emoji: '🎯', logo: '/games/carrom/Logo.png' },
  adminIcon: 'sports_score',
  adminTheme: {
    headerFrom: 'from-amber-900', headerTo: 'to-orange-800',
    ring: 'ring-amber-400', accent: 'bg-amber-600', accentHover: 'hover:bg-amber-500',
    dot: 'bg-amber-400', softBg: 'bg-amber-900/30', rangeAccent: 'accent-amber-500',
    badgeClasses: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
  },
};

const snakeLadder: GameMeta = {
  id: 'snake_ladder', displayName: 'Snake & Ladder', tag: 'Board',
  theme: { gradient: 'from-lime-500 to-green-700', emoji: '🐍', logo: '/games/snake_ladder/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-emerald-900', headerTo: 'to-lime-800',
    ring: 'ring-emerald-400', accent: 'bg-emerald-600', accentHover: 'hover:bg-emerald-500',
    dot: 'bg-emerald-400', softBg: 'bg-emerald-900/30', rangeAccent: 'accent-emerald-500',
    badgeClasses: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/30',
  },
};

// Plinko — single-player vs the house, backend-authoritative. Same
// generic ConfigPanel renders its 32 admin-tunable rows from plinko_config.
const plinko: GameMeta = {
  id: 'plinko', displayName: 'Plinko', tag: 'Arcade',
  theme: { gradient: 'from-fuchsia-500 to-purple-800', emoji: '🎯', logo: '/games/plinko/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-fuchsia-900', headerTo: 'to-purple-800',
    ring: 'ring-fuchsia-400', accent: 'bg-fuchsia-600', accentHover: 'hover:bg-fuchsia-500',
    dot: 'bg-fuchsia-400', softBg: 'bg-fuchsia-900/30', rangeAccent: 'accent-fuchsia-500',
    badgeClasses: 'bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-600/30',
  },
};

// Lucky 7 — timed, house-banked card/dice betting (3 cards: 2to6 / 7 / 8to12).
// Backend-authoritative, no PvP, no bots. The generic ConfigPanel renders its
// lucky7_config rows (master, betting, payouts, result_engine, retention,
// safety) from the widgetSchema + CATEGORY_META entries.
const lucky7: GameMeta = {
  id: 'lucky7', displayName: 'Lucky 7', tag: 'Arcade',
  theme: { gradient: 'from-red-600 to-rose-900', emoji: '🎰', logo: '/games/lucky7/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-red-900', headerTo: 'to-rose-800',
    ring: 'ring-red-400', accent: 'bg-red-600', accentHover: 'hover:bg-red-500',
    dot: 'bg-red-400', softBg: 'bg-red-900/30', rangeAccent: 'accent-red-500',
    badgeClasses: 'bg-red-600/20 text-red-300 border-red-600/30',
  },
};

// Roulette — house-banked European wheel. Per-user atomic server rounds behind
// a timed-room presentation; Target-Profit engine decides the pocket. Emerald
// house theme matching the felt.
const roulette: GameMeta = {
  id: 'roulette', displayName: 'Roulette', tag: 'Casino',
  theme: { gradient: 'from-emerald-700 to-red-900', emoji: '🎡', logo: '/games/roulette/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-emerald-900', headerTo: 'to-emerald-700',
    ring: 'ring-emerald-400', accent: 'bg-emerald-600', accentHover: 'hover:bg-emerald-500',
    dot: 'bg-emerald-400', softBg: 'bg-emerald-900/30', rangeAccent: 'accent-emerald-500',
    badgeClasses: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/30',
  },
};

// Thimbles — house-banked cups-and-ball shell game. Magenta house theme
// (matches the lobby tile + in-game stage); knobs come from the widgetSchema +
// CATEGORY_META entries (master/betting/payouts/result_engine/safety/retention).
const thimbles: GameMeta = {
  id: 'thimbles', displayName: 'Thimbles', tag: 'Arcade',
  theme: { gradient: 'from-[#a8267f] to-[#892b6c]', emoji: '🎩', logo: '/games/thimbles/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-[#5e1644]', headerTo: 'to-[#892b6c]',
    ring: 'ring-pink-400', accent: 'bg-[#a8267f]', accentHover: 'hover:bg-[#b83a90]',
    dot: 'bg-pink-400', softBg: 'bg-[#892b6c]/30', rangeAccent: 'accent-pink-500',
    badgeClasses: 'bg-[#a8267f]/20 text-pink-300 border-[#a8267f]/30',
  },
};

const cricket: GameMeta = {
  id: 'cricket', displayName: 'Cricket', tag: 'Sports',
  theme: { gradient: 'from-blue-600 to-indigo-900', emoji: '🏏', logo: '/games/cricket/Logo.png' },
  adminIcon: 'sports_cricket',
  adminTheme: {
    headerFrom: 'from-blue-900', headerTo: 'to-indigo-800',
    ring: 'ring-blue-400', accent: 'bg-blue-600', accentHover: 'hover:bg-blue-500',
    dot: 'bg-blue-400', softBg: 'bg-blue-900/30', rangeAccent: 'accent-blue-500',
    badgeClasses: 'bg-blue-600/20 text-blue-300 border-blue-600/30',
  },
};

// ─── Phase C/D — the Adrenaline family (contract C4) ─────────────────
// Three shared-table games driven by bg-tables-worker + one solo slot machine.
// Every field below is copied VERBATIM from the matching player-frontend
// games/<id>/index.ts so the lobby tile, the admin config header and the agent
// hub all show the same name/gradient/emoji. All four are games.is_locked = 1
// (dark launch) — the admin Hide/Lock matrix reads the DB, so they appear there
// automatically; this registry is what makes /settings/games/<id> resolvable
// instead of redirecting on an unknown id (GameConfigPanel.tsx :29).

// Aviator — communal crash curve. Two bet panels per player, server-side auto
// cash-out, one shared flight per table.
const aviator: GameMeta = {
  id: 'aviator', displayName: 'Aviator', tag: 'Casino',
  theme: { gradient: 'from-sky-400 to-blue-800', emoji: '✈️', logo: '/games/aviator/Logo.png' },
  adminIcon: 'flight_takeoff',
  adminTheme: {
    headerFrom: 'from-sky-900', headerTo: 'to-blue-800',
    ring: 'ring-sky-400', accent: 'bg-sky-600', accentHover: 'hover:bg-sky-500',
    dot: 'bg-sky-400', softBg: 'bg-sky-900/30', rangeAccent: 'accent-sky-500',
    badgeClasses: 'bg-sky-600/20 text-sky-300 border-sky-600/30',
  },
};

// Andar Bahar — communal two-sided shoe. One joker per round, the matching card
// decides the side; house-banked.
const andarBahar: GameMeta = {
  id: 'andar_bahar', displayName: 'Andar Bahar', tag: 'Casino',
  theme: { gradient: 'from-pink-500 to-rose-700', emoji: '🃟', logo: '/games/andar_bahar/Logo.png' },
  adminIcon: 'style',
  adminTheme: {
    headerFrom: 'from-emerald-900', headerTo: 'to-rose-800',
    ring: 'ring-rose-400', accent: 'bg-rose-600', accentHover: 'hover:bg-rose-500',
    dot: 'bg-rose-400', softBg: 'bg-rose-900/30', rangeAccent: 'accent-rose-500',
    badgeClasses: 'bg-rose-600/20 text-rose-300 border-rose-600/30',
  },
};

// Teen Patti 20-20 — communal head-to-head deal, PLAYER A vs PLAYER B at X1.98.
const teenPatti: GameMeta = {
  id: 'teen_patti', displayName: 'Teen Patti 20-20', tag: 'Casino',
  theme: { gradient: 'from-amber-500 to-rose-900', emoji: '🃏', logo: '/games/teen_patti/Logo.png' },
  adminIcon: 'style',
  adminTheme: {
    headerFrom: 'from-rose-950', headerTo: 'to-amber-700',
    ring: 'ring-amber-400', accent: 'bg-amber-600', accentHover: 'hover:bg-amber-500',
    dot: 'bg-amber-400', softBg: 'bg-amber-900/30', rangeAccent: 'accent-amber-500',
    badgeClasses: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
  },
};

// Cash Casino — the SOLO member of the family: a 3-reel slot machine, one spin =
// one atomic transaction (no table, no round clock, no worker).
const casino: GameMeta = {
  id: 'casino', displayName: 'Cash Casino', tag: 'Casino',
  theme: { gradient: 'from-yellow-400 to-red-700', emoji: '🎰', logo: '/games/casino/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-zinc-900', headerTo: 'to-yellow-700',
    ring: 'ring-yellow-400', accent: 'bg-yellow-600', accentHover: 'hover:bg-yellow-500',
    dot: 'bg-yellow-400', softBg: 'bg-yellow-900/30', rangeAccent: 'accent-yellow-500',
    badgeClasses: 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30',
  },
};

// Gems Mine — 5×5 Mines. The ONE entry here with no backend module behind it.
//
// 2026-09-06 (Phase D, plan §"Known constraints" item 10): it was missing from
// this registry while its `games` DB row has existed since migration 096, so
// admin and agent printed the raw id `gems_mine` wherever they label a game
// through `getGame(id)` — the player ledger tab, the bot detail page — and the
// Hide/Lock matrix at /settings/game-dev-security rendered a broken tile
// because it reads `games.logo_path` and neither end carried the image.
//
// It is deliberately NOT added to the BACKEND registry (`shared/src/games/
// registry.js`) — see the note at the bottom of that file. `hasAdminConfig:
// false` is how that asymmetry is stated to the UI: the tile and the name are
// real, the config page is not there yet.
//
// Fields copied VERBATIM from player-frontend/src/games/gems_mine/index.ts so
// the three ends agree, exactly as the Phase C entries below were. (`tag` here
// is the registry's own label; the DB row also says Casino, so they match.)
const gemsMine: GameMeta = {
  id: 'gems_mine', displayName: 'Gems Mine', tag: 'Casino',
  theme: { gradient: 'from-amber-700 to-emerald-900', emoji: '💎', logo: '/games/gems_mine/Logo.png' },
  adminIcon: 'diamond',
  hasAdminConfig: false,
  adminTheme: {
    headerFrom: 'from-amber-900', headerTo: 'to-emerald-800',
    ring: 'ring-amber-400', accent: 'bg-amber-600', accentHover: 'hover:bg-amber-500',
    dot: 'bg-amber-400', softBg: 'bg-amber-900/30', rangeAccent: 'accent-amber-500',
    badgeClasses: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
  },
};

// Matka King (2026-09-19) — the khelpur.com PHP app ported as a real backend
// module (shared/src/games/matka_king, migration 169). Tag matches the games
// row (Jackpot). hasAdminConfig defaults to true: /settings/games/matka_king
// renders the matka_king_config manifest; the per-market operations (times,
// multipliers, declare, exposure) live on their own admin page, /matka.
const matkaKing: GameMeta = {
  id: 'matka_king', displayName: 'Matka King', tag: 'Jackpot',
  theme: { gradient: 'from-amber-600 to-yellow-900', emoji: '🍯', logo: '/games/matka_king/Logo.png' },
  adminIcon: 'casino',
  adminTheme: {
    headerFrom: 'from-amber-900', headerTo: 'to-yellow-800',
    ring: 'ring-amber-400', accent: 'bg-amber-600', accentHover: 'hover:bg-amber-500',
    dot: 'bg-amber-400', softBg: 'bg-amber-900/30', rangeAccent: 'accent-amber-500',
    badgeClasses: 'bg-amber-600/20 text-amber-300 border-amber-600/30',
  },
};

// Order matches the player lobby's own registry order, so a tile grid built
// from this list reads the same on every end. gems_mine sits with the other
// Casino-tag games at the end of the family.
export const GAME_METAS: readonly GameMeta[] = Object.freeze([ludo, xo, rummy, chess, carrom, snakeLadder, plinko, lucky7, thimbles, cricket, roulette, aviator, andarBahar, teenPatti, casino, gemsMine, matkaKing]);

// Admin pages call this. Treats the metadata registry as authoritative
// for "does this game id exist + what's its theme/title". They never
// need the React Page (only the player-frontend renders gameplay).
export function getGame(id: string): GameMeta | undefined {
  return GAME_METAS.find((g) => g.id === id);
}

// Re-export under historical name so existing imports keep working.
export const GAMES = GAME_METAS;
